
import {
	CopilotStudyLevel,
	HashVersion,
	ICopilotBlob,
	ICopilotResponse,
	PromptVersion,
	ICopilotUserSession,
	ICopilotUserSessionDetails
} from 'interfaces';
import { CopilotAPIClient } from '@/infrastructure/copilot-api-client';
import { CopilotServiceDefaultMessage } from './defaults/copilot-service-default-message';
import { CopilotServicePingMessages } from './defaults/copilot-service-ping-messages';
import { CopilotClient } from '@/infrastructure/interfaces/CopilotClient';

declare const COPILOT_API_URL: string;
declare const COPILOT_CHAT_HISTORY_URL: string;

export class CopilotService {
	private copilotAPIClient: CopilotClient;
	private hashVersion: HashVersion = HashVersion.V2;
	private promptVersion: PromptVersion = PromptVersion.COUNTRY_CHAT_V2;

	constructor(hashVersion?: HashVersion, promptVersion?: PromptVersion) {
		if (hashVersion) {
			this.hashVersion = hashVersion;
		}

		if (promptVersion) {
			this.promptVersion = promptVersion;
		}

		this.copilotAPIClient = new CopilotAPIClient(COPILOT_API_URL, COPILOT_CHAT_HISTORY_URL);
	}

	public createNewSession(): void {
		this.copilotAPIClient = new CopilotAPIClient(COPILOT_API_URL, COPILOT_CHAT_HISTORY_URL);
	}

	public restoreSession(sessionId: string): void {
		this.copilotAPIClient = new CopilotAPIClient(COPILOT_API_URL, COPILOT_CHAT_HISTORY_URL, sessionId);
	}

	public async sendMessage(
		message: string,
		nationalityCountryISO: string,
		studyLevel: CopilotStudyLevel,
		userCurrency: string,
		contextData?: Record<string, string>,
		messageNumber: number = 1
	): Promise<ICopilotResponse> {
		const sanitizedBlob: ICopilotBlob = await this.generateSanitizedBlob(
			message,
			nationalityCountryISO,
			studyLevel,
			userCurrency,
			contextData
		);
		return await this.calculateGenerateAnswerExecTime(sanitizedBlob, messageNumber);
	}
	private async calculateGenerateAnswerExecTime(
		blob: ICopilotBlob,
		messageNumber: number = 1
	): Promise<ICopilotResponse> {
		const startTime = new Date().getTime();
		const copilotAnswer = await this.copilotAPIClient.generateAnswer(blob);
		const endTime = new Date().getTime();
		const executionTime = endTime - startTime;
		window.PostHog?.capture("copilot_api_answer_generated", {
			executionTime,
			messageNumber,
		});
		return copilotAnswer;
	}

	private async generateSanitizedBlob(
		message: string,
		nationalityCountryISO: string,
		studyLevel: CopilotStudyLevel,
		userCurrency: string,
		contextData?: Record<string, string>
	): Promise<ICopilotBlob> {

		let context = {
			studyLevel
		};

		if (contextData) {
			context = {
				...context,
				...contextData
			};
		}

		return {
			sessionId: this.copilotAPIClient.sessionId,
			context,
			nationality_country_iso: nationalityCountryISO,
			user_currency: userCurrency,
			message: await this.sanitizeMessage(message),
			promptVersion: this.promptVersion,
			hashVersion: this.hashVersion,
			trackId: window.PostHog?.get_distinct_id()
		};
	}

	public getDefaultMessage(): ICopilotResponse {
		return CopilotServiceDefaultMessage.defaults(this.promptVersion);
	}

	public getUserId(): Promise<string | void> {
		return this.copilotAPIClient.getUserId();
	}

	public getUserSessions(): Promise<ICopilotUserSession[]> {
		return this.copilotAPIClient.getUserSessions();
	}

	public getUserSession(sessionId: string): Promise<ICopilotUserSessionDetails> {
		return this.copilotAPIClient.getUserSession(sessionId);
	}

	public getPingMessages(): string[] {
		return CopilotServicePingMessages.messages(this.promptVersion);
	}

	public setHashVersion(version: HashVersion): void {
		this.hashVersion = version;
	}

	public setPromptVersion(version: PromptVersion): void {
		this.promptVersion = version;
	}

	private async sanitizeMessage(message: string): Promise<string> {
		const { default: sanitizeHtml } = await import('sanitize-html');
		return sanitizeHtml(message, {
			allowedTags: [],
			allowedAttributes: {}
		});
	}

	get currentPromptVersion(): PromptVersion {
		return this.promptVersion;
	}

	get currentSessionId(): string {
		return this.copilotAPIClient.sessionId;
	}
}
